import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { selectDetailPost } from "../service/PostApiList";
import Progress from "./Progress";
import { Viewer } from "@toast-ui/react-editor";
import "@toast-ui/editor/dist/toastui-editor-viewer.css";
import {
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {
  deleteComment,
  insertComment,
  selectCommentList,
} from "../service/CommentApiList";
import LoadingButton from "@mui/lab/LoadingButton";

const API_BASE = process.env.REACT_APP_APIURL; // 예: http://localhost:3001

const Post = () => {
  const navigate = useNavigate();
  const params = useParams().id;
  const postID = Number(params);

  const [progress, setProgress] = useState(true);
  const [comment, setComment] = useState({
    postID,
    author: null,
    content: null,
    parentnode: 0,
  });
  const [size, setSize] = useState({ width: 0, height: 0 });
  const admin = window.sessionStorage.getItem("admin");
  const viewerRef = useRef();

  const [postData, setPostData] = useState({
    id: postID,
    title: "",
    parentnode: null,
    category: "P",
    content: null,
    regdate: null,
    ispublic: false,
  });

  const [thumbUrl, setThumbUrl] = useState(null); // ← 썸네일 URL
  const [loading, setLoading] = useState(false);
  const [commentList, setCommentList] = useState([]);

  const [thumbFailed, setThumbFailed] = useState(false);

  const thumb = (id, w = 480) => {
    // 캐시 버전 파라미터(v)로 자동 무효화
    return `${API_BASE}/posts/${id}/thumbnail?w=${w}&fmt=webp`;
  };

  const initializePage = async () => {
    await Promise.all([fn1(), fn2()]);
    setProgress(false);
  };

  const fn1 = async () => {
    const result = await selectDetailPost(postID);
    const temp = result.data;

    if ((temp.id && temp.id === "undefined") || temp.category === "C") {
      alert("정상적인 접근이 아닙니다. 이전 페이지로 이동합니다.");
      navigate(-1);
      return;
    }

    const reg = new Date(temp.regdate).toLocaleDateString();

    setPostData((prev) => ({
      ...prev,
      title: temp.title,
      parentnode: temp.parentnode,
      category: temp.category,
      content: temp.content,
      regdate: reg,
      ispublic: temp.ispublic,
    }));

    // 썸네일 URL(이미지 전용 엔드포인트 활용)
    setThumbUrl(thumb(postID, temp.regdate));

    // Viewer 세팅 (안전 접근)
    viewerRef.current?.getInstance?.().setMarkdown(temp.content || "");
  };

  const fn2 = async () => {
    const commentResult = await selectCommentList(postID);
    setCommentList(commentResult.data);
  };

  useEffect(() => {
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setComment({
      postID,
      author: "",
      content: "",
      parentnode: 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentList]);

  // 원본 이미지 크기 가져오기 (선택)
  useEffect(() => {
    if (!thumbUrl) return;
    const img = new Image();
    img.onload = () => setSize({ width: img.width, height: img.height });
    img.src = thumbUrl;
  }, [thumbUrl]);

  const handleInsertComment = async () => {
    setLoading(true);
    await insertComment(comment);
    await fn2();
    setLoading(false);
  };

  const handleDeleteComment = async (e) => {
    await deleteComment(e.target.id);
    await fn2();
  };

  const handleChangeComment = (e) => {
    const { value, name } = e.target;
    setComment({ ...comment, [name]: value });
  };

  return (
    <>
      <Progress progress={progress} />
      <Box id="content" sx={{ bgcolor: "rgba(178, 144, 121, 0.3)", p: 2 }}>
        <div className="_prevPage">
          <Link to={"/posts/" + postData.parentnode}>상위 카테고리로 이동</Link>
        </div>
        <Box>
          <Typography>{postData.title}</Typography>
        </Box>

        {thumbUrl &&
          (thumbFailed ? (
            ""
          ) : (
            <Box sx={{ mb: 2 }}>
              <img
                src={thumbUrl}
                alt="썸네일"
                loading="lazy"
                // 실패 시 플레이스홀더로 폴백
                onError={() => {
                  setThumbFailed(true);
                }}
                style={{
                  width: size.width || "100%",
                  height: size.height ? `${size.height}px` : "auto",
                  maxWidth: "100%",
                  objectFit: "contain",
                }}
                // 반응형 소스(선택)
                srcSet={[
                  `${API_BASE}/posts/${postID}/thumbnail?w=240&fmt=webp 240w`,
                  `${API_BASE}/posts/${postID}/thumbnail?w=320&fmt=webp 320w`,
                  `${API_BASE}/posts/${postID}/thumbnail?w=480&fmt=webp 480w`,
                  `${API_BASE}/posts/${postID}/thumbnail?w=640&fmt=webp 640w`,
                ].join(", ")}
                sizes="(max-width: 600px) 240px, (max-width: 900px) 320px, 480px"
              />
            </Box>
          ))}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Viewer initialValue={postData.content || ""} ref={viewerRef} />
        </Paper>

        {typeof admin === "undefined" || admin === null || admin === "" ? (
          ""
        ) : (
          <div className="_postContentUpdate">
            <Link
              to={"/post/edit"}
              state={{
                id: postID,
                parentnode: postData.parentnode,
                isNew: false,
              }}
            >
              게시글 수정
            </Link>
          </div>
        )}

        <Box sx={{ mt: 2, mb: 2, bgcolor: "white" }}>
          {commentList.map((v, idx) => (
            <div key={idx}>
              <Grid
                container
                item
                xs={12}
                justifyContent="center"
                alignItems="center"
                sx={{ p: 2 }}
              >
                <Grid
                  item
                  xs={2}
                  sx={{ display: "flex", justifyContent: "left" }}
                >
                  {v.author}
                </Grid>
                <Grid item xs={7}>
                  <Typography>{v.content}</Typography>
                </Grid>
                <Grid
                  item
                  xs={2}
                  sx={{ display: "flex", justifyContent: "right" }}
                >
                  {new Date(v.regdate).toLocaleDateString()}
                </Grid>
                <Grid
                  item
                  xs={1}
                  sx={{ display: "flex", justifyContent: "center" }}
                >
                  {typeof admin === "undefined" ||
                  admin === null ||
                  admin === "" ? (
                    ""
                  ) : (
                    <Button
                      size="small"
                      id={v.id}
                      onClick={handleDeleteComment}
                    >
                      댓글 삭제
                    </Button>
                  )}
                </Grid>
              </Grid>
              <Divider />
            </div>
          ))}

          <Grid
            container
            sx={{ alignItems: "center", justifyContent: "space-between", p: 2 }}
            spacing={2}
          >
            <Grid item xs={2}>
              <TextField
                label="작성자"
                variant="standard"
                name="author"
                fullWidth
                placeholder="작성자 입력"
                value={comment.author}
                onChange={handleChangeComment}
              />
            </Grid>
            <Grid item xs={8}>
              <TextField
                label="댓글"
                name="content"
                multiline
                fullWidth
                rows={1}
                placeholder="댓글 입력"
                value={comment.content}
                onChange={handleChangeComment}
              />
            </Grid>
            <Grid
              item
              xs={2}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <LoadingButton
                loading={loading}
                loadingIndicator="Sending…"
                variant="contained"
                size="large"
                disabled={!comment.content || !comment.author}
                onClick={handleInsertComment}
              >
                댓글 작성
              </LoadingButton>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default Post;
